import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { vendorDepositApi } from "../../api/vendorDepositApi";
import { toast } from "react-toastify";
import { useUser } from "../../hooks/useUser";
import {
  getVendorExpenses,
  getDepositExpenses,
} from "../../api/vendorExpenseApi";
import SafeImage from "../../components/common/SafeImage";
import CloseIcon from "@mui/icons-material/Close";
import apiClient from "../../api/axios";
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
/**
 * VendorDepositCash Component
 * Dashboard for managing vendor petty cash deposits
 * Shows all deposits for admin users, only vendor's own deposits for vendor users
 */
const VendorDepositCash = () => {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const [deposits, setDeposits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [depositToDeleteId, setDepositToDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSlider, setOpenSlider] = useState(null);
  const [openDetailsId, setOpenDetailsId] = useState(null);
  const dropdownRefs = useRef({});
  const [syncing, setSyncing] = useState(false);

  // Check if user is vendor
  const isVendor = user?.role === "vendor";
  const isAdmin = user?.role === "employee" || user?.role === "admin";



  // Load vendor deposits with optional vendor filtering
  const loadDeposits = useCallback(
    async (vendorId = null) => {
      // For vendor users, only show their own deposits
      if (isVendor) {
        setLoading(true);
        setError(null);
        try {
          const response = await vendorDepositApi.getVendorDeposits();
          if (response.success) {
            setDeposits(response.data);
          } else {
            throw new Error(response.message || "Failed to load deposits");
          }
        } catch (err) {
          const errorMessage = err.message || "An unknown error occurred.";
          setError(errorMessage);
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
        return;
      }

      // For admin users, show all deposits with optional filtering
      if (!isAdmin) {
        setError("You are not authorized to view this page vithu .");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await vendorDepositApi.getDeposits(vendorId);
        if (response.success) {
          setDeposits(response.data);
        } else {
          throw new Error(response.message || "Failed to load deposits");
        }
      } catch (err) {
        const errorMessage = err.message || "An unknown error occurred.";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user, isVendor, isAdmin],
  );

  // Load vendor list for dropdown filtering (admin only)
  const loadVendors = async () => {
    if (!isAdmin) return;

    try {
      const response = await vendorDepositApi.getVendors();
      if (response.success) setVendors(response.data);
      else toast.error(response.message || "Failed to load vendors");
    } catch (err) {
      toast.error(err.message || "Failed to load vendors");
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadVendors();
    }
  }, [isAdmin]);

  useEffect(() => {
    loadDeposits(selectedVendor || null);
  }, [selectedVendor, loadDeposits]);

  // Handle delete confirmation modal display
  const handleDeleteClick = (id) => {
    setDepositToDeleteId(id);
    setShowModal(true);
  };

  // Execute vendor deposit deletion and update local state
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await vendorDepositApi.deleteDeposit(depositToDeleteId);
      if (response.success) {
        toast.success("Deposit deleted successfully");
        setDeposits((prev) =>
          prev.filter((d) => d.deposit_id !== depositToDeleteId),
        );
      } else {
        toast.error(response.message || "Failed to delete deposit");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete deposit");
    } finally {
      setShowModal(false);
      setDepositToDeleteId(null);
      setDeleteLoading(false);
    }
  };

  // Cancel delete operation and close modal
  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setShowModal(false);
    setDepositToDeleteId(null);
  };

  // Retry loading data after error
  const handleRetry = () => {
    loadDeposits(selectedVendor || null);
  };

  // Show detailed view for selected deposit
  const handleViewClick = (deposit) => {
    setSelectedDeposit(deposit);
  };

  // Close detailed deposit view
  const handleViewClose = () => {
    setSelectedDeposit(null);
  };

  // Toggle dropdown menu for deposit actions
  const handleDropdownToggle = (depositId) => {
    setOpenDropdown(openDropdown === depositId ? null : depositId);
    setOpenSlider(openSlider === depositId ? null : depositId);
  };

  // TransactionDetails for Vendor Expense History
  const TransactionDetails = React.memo(function TransactionDetails({
    name,
    balance,
    vendorId,
    depositId,
    onClose,
    currencySymbol,
    onRefreshDeposits,
  }) {
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [error, setError] = useState(null);

    const urlParams = new URLSearchParams(location.search);
    const depositIdFromUrl = urlParams.get("deposit_id"); // existing
    const expenseIdFromUrl = urlParams.get("expense_id"); // new for editing

    useEffect(() => {
      const fetchExpenses = async () => {
        try {
          setLoading(true);
          // Use deposit-specific expenses if depositId is provided
          const data = depositId
            ? await getDepositExpenses(depositId)
            : await getVendorExpenses(vendorId);
          setExpenses(data);
          setError(null);
        } catch {
          setError("Failed to load expense history");
          toast.error("Failed to load expense history");
        } finally {
          setLoading(false);
        }
      };
      if (vendorId || depositId) fetchExpenses();
    }, [vendorId, depositId]);

    // Sort oldest first for display
    const expensesSorted = useMemo(
      () =>
        [...expenses].sort(
          (a, b) => new Date(a.expense_date) - new Date(b.expense_date),
        ),
      [expenses],
    );

    // Fallback: calculate running balance if missing in any expense
    const expensesWithBalance = useMemo(() => {
      let running = parseFloat(balance);
      const withBalance = expensesSorted.map((exp) => {
        let expBalance = exp.balance;
        if (typeof expBalance === "undefined" || expBalance === null) {
          running -= parseFloat(exp.amount);
          expBalance = running;
        }
        return { ...exp, balance: expBalance };
      });
      return withBalance;
    }, [expensesSorted, balance]);

    const currentBalance = useMemo(() => {
      if (expensesWithBalance.length > 0) {
        const last =
          expensesWithBalance[expensesWithBalance.length - 1].balance;
        return typeof last === "number" ? last : parseFloat(last) || 0;
      }
      return parseFloat(balance) || 0;
    }, [expensesWithBalance, balance]);
    
  //  delete expense
  const handleExpenseDelete = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;

    try {
      const url = isAdmin
        ? `/vendor-expenses/${expenseId}`      // admin route
        : `/vendor/expenses/${expenseId}`;     // vendor route

      const res = await apiClient.delete(url);

      toast.success(
        res?.data?.message || "Expense record deleted successfully"
      );
           
      //refetch data
      onRefreshDeposits?.();
      // update UI
      setExpenses((prev) =>
        prev.filter((e) => e.vendor_expense_id !== expenseId)
      );

    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete expense"
      );
    }
  };

    return (
  <div
    className="p-3 border-top details-slider  shadow-sm rounded-3"
    style={{ transition: "all 0.3s cubic-bezier(.4,0,.2,1)", background:"#ffffffc4"}}
  >
    <div className="d-flex justify-content-between mb-2" style={{color:"#000000"}}>
      <div>
        <strong>Vendor:</strong> {name}
      </div>
      <div>
        <strong>Current Balance:</strong>{" "}
        {typeof currentBalance === "number" && !isNaN(currentBalance)
          ? `${currencySymbol || ""} ${currentBalance.toFixed(2)}`
          : "-"}
      </div>
    </div>

    <hr />

    <h6 className="mb-3" style={{color:"#000000"}}>
      Expense History {depositId && `(Deposit ${depositId})`}
    </h6>

    <div style={{ maxWidth: "100%", overflowX: "auto" }}>
      {loading ? (
        <div className="text-center py-3">
          <div
            className="skeleton-loader mx-auto"
            style={{
              height: 32,
              borderRadius: 6,
              width: "80%",
              background:
                "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "skeleton 1.2s infinite linear",
            }}
          />
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <table className="table table-sm mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="text-end">Expense Amount</th>
              <th className="text-end">Balance</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expensesWithBalance.length > 0 ? (
              expensesWithBalance.map((expense) => (
                <tr key={expense.vendor_expense_id || Math.random()}>
                  <td>
                    {expense.expense_date
                      ? new Date(expense.expense_date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{expense.description || "-"}</td>
                  <td className="text-danger fw-bold text-end">
                    {expense.amount
                      ? `-${currencySymbol || ""} ${parseFloat(
                          expense.amount
                        ).toFixed(2)}`
                      : "-"}
                  </td>
                  <td className="fw-semibold text-end">
                    {typeof expense.balance !== "undefined" &&
                    expense.balance !== null
                      ? `${currencySymbol || ""} ${parseFloat(
                          expense.balance
                        ).toFixed(2)}`
                      : "-"}
                  </td>
                  <td className="text-center d-flex justify-content-center gap-1">
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() =>
                        navigate(
                          isAdmin
                            ? `/accounts/add-vendor-expense?expense_id=${expense.vendor_expense_id}&deposit_id=${depositId}`
                            : `/accounts/add-vendor-expense-vendor?deposit_id=${depositId}`,
                          { state: { expense, depositId } }
                        )
                      }
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleExpenseDelete(expense.vendor_expense_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-3">
                  No expenses found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>

    <div className="d-flex justify-content-end mt-3">
      <button className="btn btn-link" onClick={onClose}>
        Close
      </button>
    </div>

    <style>{`
      @keyframes skeleton {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      .details-slider {
        animation: slideDown 0.3s cubic-bezier(.4,0,.2,1);
      }
      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  </div>
    );
  });

  // Sync deposits with expense history
  const handleSyncDeposits = async () => {
    setSyncing(true);
    try {
      const response = await vendorDepositApi.syncDeposits();
      if (response.success) {
        toast.success("Deposits synced successfully");
        loadDeposits();
      } else {
        toast.error(response.message || "Failed to sync deposits");
      }
    } catch (err) {
      toast.error(err.message || "Failed to sync deposits");
    } finally {
      setSyncing(false);
    }
  };

  if (error) {
    return (
      <div className="alert alert-danger text-center">
        <h5>Error Loading Data</h5>
        <p>{error}</p>
        {error !== "You are not authorized to view this page." && (
          <button className="btn btn-primary" onClick={handleRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h2 className="fw-bold">
          {isVendor ? "My Vendor Deposits" : "Vendor Deposit Cash"}
        </h2>
        <div className="d-flex gap-2">
          {isAdmin && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/accounts/add-vendor-deposit-cash")}
              >
                Add Vendor Deposit
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/accounts/add-vendor-expense")}
              >
                Add Vendor Expense
              </button>
              <button
                className="btn btn-info"
                onClick={handleSyncDeposits}
                disabled={syncing}
                title="Sync Deposits"
              >
                {syncing ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-sync-alt"></i>
                )}
              </button>
            </>
          )}
          {isVendor && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/accounts/add-vendor-expense-vendor")}
              >
                Add My Expense
              </button>
            </>
          )}
        </div>
      </div>
      {/* Vendor filter - only show for admin users */}
      {isAdmin && (
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="vendorFilter" className="form-label">
              Filter by Vendor:
            </label>
            <select
              id="vendorFilter"
              className="form-select"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              disabled={loading}
            >
              <option value="" disabled>
                All Vendors
              </option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.vendor_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-striped theme-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vendor Name</th>
              <th>Date</th>
              <th>Petty Cash</th>
              <th>Expense</th>
              <th>Balance</th>
              <th>Preview</th>
              <th>Add Expense</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <tr key={index}>
                  <td>
                    <span className="placeholder col-10"></span>
                  </td>
                  <td>
                    <span className="placeholder col-12"></span>
                  </td>
                  <td>
                    <span className="placeholder col-8"></span>
                  </td>
                  <td>
                    <span className="placeholder col-10"></span>
                  </td>
                  <td>
                    <span className="placeholder col-10"></span>
                  </td>
                  <td>
                    <span className="placeholder col-10"></span>
                  </td>
                  <td>
                    <span className="placeholder col-6"></span>
                  </td>
                  <td>
                    <ul className="d-flex gap-2 justify-content-center">
                      <li>
                        <span
                          className="placeholder"
                          style={{
                            width: "60px",
                            height: "30px",
                            borderRadius: "5px",
                          }}
                        ></span>
                      </li>
                      <li>
                        <span
                          className="placeholder"
                          style={{
                            width: "60px",
                            height: "30px",
                            borderRadius: "5px",
                          }}
                        ></span>
                      </li>
                    </ul>
                  </td>
                </tr>
              ))
            ) : deposits.length > 0 ? (
              deposits.map((deposit) => (
                <React.Fragment key={deposit.deposit_id}>
                  <tr>
                    <td>{deposit.deposit_id}</td>
                    <td>
                      {deposit.vendor_name ||
                        deposit.vendor?.user?.name ||
                        deposit.vendor?.vendor_name ||
                        "N/A"}
                    </td>
                    <td>
                      {new Date(deposit.deposit_date).toLocaleDateString()}
                    </td>
                    <td>
                      <span className="text-success">
                        {parseFloat(deposit.petty_cash) > 0 ? deposit.currency_symbol + " " : ""} {parseFloat(deposit.petty_cash).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="text-danger">
                        {parseFloat(deposit.expense) > 0 ? deposit.currency_symbol + " " : ""} {parseFloat(deposit.expense).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="text-primary fw-bold">
                        {parseFloat(deposit.balance) > 0 ? deposit.currency_symbol + " " : ""} {parseFloat(deposit.balance).toFixed(2)}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {deposit.receipt_image ? (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          style={{
                            width: "60px",
                            height: "30px",
                            fontSize: "12px",
                            border: "2px solid rgb(0, 195, 133)",
                          }}
                          onClick={() => handleViewClick(deposit)}
                          type="button"
                        >
                          View
                        </button>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    {/* Add Expense button - visible to admin and vendor */}
                    <td>
                      {isAdmin ? (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() =>
                            navigate(
                              `/accounts/add-vendor-expense?deposit_id=${deposit.deposit_id}`,
                            )
                          }
                        >
                          Add Expense
                        </button>
                      ) : (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() =>
                            navigate(
                              `/accounts/add-vendor-expense-vendor?deposit_id=${deposit.deposit_id}`,
                            )
                          }
                        >
                          Add Expense
                        </button>
                      )}
                    </td>

                    <td>
                      <ul className="d-flex gap-2 justify-content-center">
                        {isAdmin && (
                          <li>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid rgb(0, 195, 133)",
                              }}
                              onClick={() =>
                                navigate(
                                  `/accounts/edit-vendor-deposit-cash/${deposit.deposit_id}`,
                                )
                              }
                            >
                              Edit
                            </button>
                          </li>
                        )}
                        {isAdmin && (
                          <li>
                            <button
                              className="btn btn-danger btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid #dc3545",
                              }}
                              onClick={() =>
                                handleDeleteClick(deposit.deposit_id)
                              }
                            >
                              Delete
                            </button>
                          </li>
                        )}
                        <li
                          className="dropdown"
                          style={{ position: "relative" }}
                        >
                          <button
                            className="btn btn-secondary btn-sm dropdown-toggle"
                            type="button"
                            onClick={() =>
                              setOpenDetailsId(
                                openDetailsId === deposit.deposit_id
                                  ? null
                                  : deposit.deposit_id,
                              )
                            }
                            aria-expanded={openDetailsId === deposit.deposit_id}
                            style={{
                              width: "40px",
                              height: "30px",
                              fontSize: "16px",
                              border: "2px solid #6c757d",
                            }}
                          >
                            ⋮
                          </button>
                        </li>
                      </ul>
                    </td>
                  </tr>
                  {openDetailsId === deposit.deposit_id && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ background: "#f8f9fa", padding: 0 }}
                      >
                        <TransactionDetails
                          name={
                            deposit.vendor_name ||
                            deposit.vendor?.user?.name ||
                            deposit.vendor?.vendor_name ||
                            "N/A"
                          }
                          balance={parseFloat(deposit.petty_cash)}
                          vendorId={deposit.vendor_id}
                          depositId={deposit.deposit_id}
                          onClose={() => setOpenDetailsId(null)}
                          currencySymbol={deposit.currency_symbol}
                           onRefreshDeposits={loadDeposits}  
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  No deposits found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Modal - only show for admin users */}
      {showModal && isAdmin && (
        <>
          <style>{`
                      .modal-footer .btn-cancel {
                        background: #6c75a9;
                        color: #fff;
                        border: none;
                        transition: background 0.2s;
                      }
                      .modal-footer .btn-cancel:hover, .modal-footer .btn-cancel:focus {
                        background: #b0b3bb !important;
                        color: #333 !important;
                      }
                      .modal-footer .btn-delete {
                        background: #e74c3c;
                        color: #fff;
                        border: none;
                        transition: background 0.2s;
                      }
                      .modal-footer .btn-delete:hover, .modal-footer .btn-delete:focus {
                        background: #ffb3b3 !important;
                        color: #b71c1c !important;
                      }
                    `}</style>
          <div
            className="modal fade show"
            style={{ display: "block", zIndex: 2000 }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>


                  <button
                    type="button"
                    className="view-close-btn"
                    onClick={handleDeleteCancel}
                    disabled={deleteLoading}
                  ><CloseIcon /></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                    <h4>Are you sure?</h4>
                    <p>
                      Do you really want to delete this deposit? <br /> This
                      action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="modal-footer d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleDeleteCancel}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={handleDeleteConfirm}
                    disabled={deleteLoading}
                    style={{ minWidth: 90 }}
                  >
                    {deleteLoading ? (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                    ) : null}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
          ></div>
        </>
      )}

      {/* Receipt Preview Modal */}
      {selectedDeposit && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", zIndex: 2100 }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deposit Details</h5>
                  <style>{`
  .view-close-btn {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #ffffff;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  }

  .view-close-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }

  .view-close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .view-close-btn svg {
    font-size: 20px;
  }
`}</style>

                  <button
                    type="button"
                    className="view-close-btn"
                    onClick={handleViewClose}
                  ><CloseIcon /></button>
                </div>
                <div className="modal-body">
                  {selectedDeposit.receipt_image && (
                    <div className="text-center mb-4">
                      <h6 className="mb-3">Receipt Image</h6>
                      <a
                        href={selectedDeposit.receipt_image}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SafeImage
                          src={selectedDeposit.receipt_image}
                          alt="Receipt"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            border: "2px solid #e9ecef",
                          }}
                        />
                      </a>
                      <div className="mt-2">
                        <small className="text-muted">
                          Click image to view full size
                        </small>
                      </div>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-primary mb-3">Basic Information</h6>
                        <div className="mb-2">
                          <strong>ID:</strong> {selectedDeposit.deposit_id}
                        </div>
                        <div className="mb-2">
                          <strong>Vendor Name:</strong>{" "}
                          {selectedDeposit.vendor_name ||
                            selectedDeposit.vendor?.user?.name ||
                            selectedDeposit.vendor?.vendor_name ||
                            "N/A"}
                        </div>
                        <div className="mb-2">
                          <strong>Date:</strong>{" "}
                          {new Date(
                            selectedDeposit.deposit_date,
                          ).toLocaleDateString()}
                        </div>
                        <div className="mb-2">
                          <strong>Payment Method:</strong>{" "}
                          {selectedDeposit.payment_method}
                        </div>
                        <div className="mb-2">
                          <strong>Receipt Number:</strong>{" "}
                          {selectedDeposit.receipt_number || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-success mb-3">Financial Details</h6>
                        <div className="mb-2">
                          <strong>Petty Cash:</strong>{" "}
                          <span className="text-success">
                            {parseFloat(selectedDeposit.petty_cash) > 0
                              ? selectedDeposit.currency_symbol + " "
                              : ""} {parseFloat(selectedDeposit.petty_cash).toFixed(2)}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Expense:</strong>{" "}
                          <span className="text-danger">
                            {parseFloat(selectedDeposit.expense) > 0
                              ? selectedDeposit.currency_symbol + " "
                              : ""} {parseFloat(selectedDeposit.expense).toFixed(2)}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Balance:</strong>{" "}
                          <span className="text-primary fw-bold">
                            {parseFloat(selectedDeposit.balance) > 0
                              ? selectedDeposit.currency_symbol + " "
                              : ""} {parseFloat(selectedDeposit.balance).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {selectedDeposit.notes && (
                      <div className="col-12">
                        <div className="border rounded p-3">
                          <h6 className="text-info mb-3">Notes</h6>
                          <div style={{ whiteSpace: "pre-line" }}>
                            {selectedDeposit.notes}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleViewClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 2050 }}
          ></div>
        </>
      )}
    </>
  );
};

export default VendorDepositCash;

